import { addDays, format, isBefore, parse } from 'date-fns'
import type { WorkingTimeData, WorkingTimeDays, WorkingTimeDetails } from '@nsftx/systems-sdk'

export const getScheduleTimeForCurrentValidTime = ({ end }: WorkingTimeDetails) => {
  const now = new Date()
  const [targetHours, targetMinutes, targetSeconds] = end.split(':').map(Number)
  const targetDate = new Date(now)
  /**
   * We add +1 second because when valid time is for example 08:00:00 - 12:00:00
   * Current time is 11:00:00 (valid)
   * We take end time => 12:00:00
   * And we add one sec => 12:00:01 so this becomes time when it is not valid anymore and
   * we need to trigger the timer again.
   */
  targetDate.setHours(targetHours ?? 0, targetMinutes ?? 0, (targetSeconds ?? 0) + 1, 0)

  return targetDate.getTime() - now.getTime()
}

export const getNextDayMidnightScheduleTime = () => {
  const now = new Date()
  const nextDay = addDays(now, 1)
  nextDay.setHours(0, 0, 0, 0)
  return nextDay.getTime() - now.getTime()
}

export const getScheduleTimeForCurrentInvalidTime = (
  validTimesPerDay: Array<WorkingTimeDetails>
) => {
  const now = new Date()
  const TIME_FORMAT = 'HH:mm:ss'
  const currentTime = format(new Date(), TIME_FORMAT)
  const currentParsedTime = parse(currentTime, TIME_FORMAT, now)

  /**
   * We first need to figure out what is next valid time (if it exist)
   * Ex:
   * Valid times per day are:
   *    08:00:00 - 12:00:00
   *    14:00:00 - 19:00:00
   *    20:00:00 - 21:00:00
   *
   * Current time is: 13:00:00 (which is invalid time)
   * We need to find next valid time from the array above to know when to trigger working time validation again
   * In this case it is second one (14:00:00 - 19:00:00)
   */

  const nextValidTime: WorkingTimeDetails | undefined = validTimesPerDay.find(({ start }) => {
    const startTime = parse(start, TIME_FORMAT, now)

    return isBefore(currentParsedTime, startTime)
  })

  if (nextValidTime) {
    const [targetHours, targetMinutes, targetSeconds] = nextValidTime.start.split(':').map(Number)

    const targetDate = new Date(now)
    targetDate.setHours(targetHours ?? 0, targetMinutes ?? 0, targetSeconds ?? 0, 0)

    return targetDate.getTime() - now.getTime()
  }
  /**
   * In case there is no next valid time.
   * Ex. if we take examples from the comment above and say currently it is 22:00:00 h
   * Then we schedule the checker for 00:00:00 of next day
   */

  return getNextDayMidnightScheduleTime()
}

export const getNextValidTime = (workingTimeData: WorkingTimeData): string | undefined => {
  const now = new Date()
  const DATE_TIME_FORMAT = 'HH:mm (dd.MM.yyyy)'
  const TIME_FORMAT = 'HH:mm:ss'
  const currentDayIndex = now.getDay() // 0 (Sunday) to 6 (Saturday)
  const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

  const currentDay = weekDays[currentDayIndex]
  const validTimesPerDay = workingTimeData[`working_time_${currentDay}` as WorkingTimeDays]
  const currentTime = format(now, TIME_FORMAT)
  const currentParsedTime = parse(currentTime, TIME_FORMAT, now)

  // 1. Check for the next valid time in today’s schedule
  const nextValidTimeToday = validTimesPerDay?.find(({ start }) => {
    const startTime = parse(start, TIME_FORMAT, now)
    return isBefore(currentParsedTime, startTime)
  })

  if (nextValidTimeToday) {
    const [targetHours, targetMinutes, targetSeconds] = nextValidTimeToday.start
      .split(':')
      .map(Number)
    const targetDate = new Date(now)
    targetDate.setHours(targetHours ?? 0, targetMinutes ?? 0, targetSeconds ?? 0, 0)
    return format(targetDate, DATE_TIME_FORMAT)
  }

  // 2. Find the next day with a valid working time
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7
    const nextDay = weekDays[nextDayIndex]
    const nextDayTimes = workingTimeData[`working_time_${nextDay}` as WorkingTimeDays]

    if (nextDayTimes && nextDayTimes.length > 0 && nextDayTimes[0]?.start) {
      const nextStartTime = nextDayTimes[0].start // Take the first working period
      const [targetHours, targetMinutes, targetSeconds] = nextStartTime.split(':').map(Number)
      const targetDate = addDays(now, i)
      targetDate.setHours(targetHours ?? 0, targetMinutes ?? 0, targetSeconds ?? 0, 0)
      return format(targetDate, DATE_TIME_FORMAT)
    }
  }

  return undefined
}
